import { useContext, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  Alert,
  AlertIcon,
  Box,
  AlertDescription,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import TextFieldInput from 'components/fields/auto/TextFieldInput';
import FormInput from 'components/fields/auto/FormInput';
import DateInput from 'components/fields/auto/DateInput';
import RadioInput from 'components/fields/auto/RadioInput';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';
import Divider from 'views/auto_form/components/Divider';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';
import getValidationMessage from 'helpers/getValidationMessage';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getDateString from 'helpers/getDateString';

import {
  changeUnlistedSpouse,
  addOrUpdateRemark,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { SECTIONS } from 'views/auto_form/constants';

const UnlistedSpouses = () => {
  const { uuid } = useParams();
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();

  // Color mode values for section headers
  const sectionBg = useColorModeValue('#ccc', 'gray.600');
  const sectionColor = useColorModeValue('black', 'white');

  const spouses = useAppSelector(
    (state) => state.autoPersonalForm.data.unlisted_spouses,
  );

  const spouseRemarks = useAppSelector((state) =>
    state.autoPersonalForm.remarks.filter(
      (r: any) =>
        r.validation_key === '022' && r.item_key.startsWith('spouse-'),
    ),
  );

  const validation = validations.find((v) => v.validation_id === '022');

  useEffect(() => {
    spouses.forEach((spouse, index) => {
      if (spouse.is_in_household === false) {
        dispatch(
          addOrUpdateRemark({
            validation_key: '022',
            item_key: `spouse-${index + 1}`,
            data: {
              text: spouse.no_reason || '',
              section: SECTIONS.DRIVERS,
            },
          }),
        );
      } else if (spouse.is_in_household === true) {
        if (spouse.is_licensed === false) {
          dispatch(
            addOrUpdateRemark({
              validation_key: '022',
              item_key: `spouse-${index + 1}`,
              data: {
                text: `Spouse: ${
                  spouse.name || '???'
                } - Is this spouse still in the household?: Yes - Spouse is not licensed`,
              },
            }),
          );
        } else if (spouse.is_licensed === true) {
          dispatch(
            addOrUpdateRemark({
              validation_key: '022',
              item_key: `spouse-${index + 1}`,
              data: {
                text: `Spouse: ${
                  spouse.name || '???'
                } - Is this spouse still in the household?: Yes - License: ${
                  spouse.license_number || 'N/A'
                } - Carrier: ${spouse.carrier || 'N/A'} - Policy #: ${
                  spouse.policy || 'N/A'
                } - Expiry Date: ${spouse.expiry_date || 'N/A'}`,
              },
            }),
          );
        }
      }
    });
  }, [spouses, dispatch]);

  useEffect(() => {
    spouses.forEach((spouse, index) => {
      const subLabel = `Spouse ${index + 1}`;

      if (spouse.is_in_household !== false) {
        dispatch(removeMissingMandatoryField(`Explanation (${subLabel})`));
      }

      if (spouse.is_in_household !== true) {
        dispatch(
          removeMissingMandatoryField(`Is this spouse licensed? (${subLabel})`),
        );
      }

      if (spouse.is_in_household !== true || spouse.is_licensed !== true) {
        dispatch(removeMissingMandatoryField(`License (${subLabel})`));
        dispatch(removeMissingMandatoryField(`Carrier (${subLabel})`));
        dispatch(removeMissingMandatoryField(`Policy # (${subLabel})`));
        dispatch(removeMissingMandatoryField(`Expiry Date (${subLabel})`));
      }
    });
  }, [spouses, dispatch]);

  const onToggleChange = (index: number, status: boolean) => {
    dispatch(
      changeUnlistedSpouse({ name: 'is_in_household', value: status, index }),
    );
  };

  const onLicensedChange = (index: number, status: boolean) => {
    dispatch(
      changeUnlistedSpouse({ name: 'is_licensed', value: status, index }),
    );
  };

  const onSpousesChange = (event: any, index: number) => {
    const { name, value } = event.target;

    dispatch(changeUnlistedSpouse({ name, value, index }));
  };

  const onExplanationChange = (event: any, index: number) => {
    const { value } = event.target;

    dispatch(changeUnlistedSpouse({ name: 'no_reason', value, index }));

    dispatch(
      addOrUpdateRemark({
        validation_key: '022',
        item_key: `spouse-${index + 1}`,
        data: {
          text: value,
          text_validator_log_id: null,
          ai_result: '',
          status: 'created',
          section: SECTIONS.DRIVERS,
        },
      }),
    );
  };

  const onDateChange = (date: Date, name: string, index: number) => {
    dispatch(
      changeUnlistedSpouse({
        name,
        index,
        value: getDateString(date),
      }),
    );
  };

  if (!validation || !spouses) return null;

  return (
    <Box mb="20px">
      <Divider mb="10px">Unlisted spouses</Divider>

      <Alert status="warning" mb="20px" fontSize="14px">
        <AlertIcon />
        <Box w="100%">
          <AlertDescription>
            <ValidationMessage message={getValidationMessage(validation)} />
          </AlertDescription>
        </Box>
      </Alert>

      <Box pl="20px">
        {spouses.map((spouse, index: number) => {
          return (
            <Box mt={index !== 0 ? '20px' : ''} key={index}>
              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
              >
                <strong>({index + 1}) Spouse: </strong> {spouse.name || '???'}
              </Box>

              <Flex mt="10px" mb="10px" gap="20px">
                <Box w="30%">
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Name"
                    subLabel={`Spouse ${index + 1}`}
                    name="name"
                    value={spouse.name}
                    onChange={(e) => onSpousesChange(e, index)}
                    required
                  />
                </Box>
                <Box w="70%" pt="35px">
                  <RadioInput
                    label="Is this spouse still in the household?"
                    subLabel={`Spouse ${index + 1}`}
                    section={SECTIONS.DRIVERS}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(spouse.is_in_household)}
                    onChange={(value) =>
                      onToggleChange(index, value === 'true')
                    }
                    required
                  />
                </Box>
              </Flex>

              {spouse.is_in_household === false && (
                <Box>
                  <TextFieldInput
                    section={SECTIONS.DRIVERS}
                    label="Explanation"
                    subLabel={`Spouse ${index + 1}`}
                    name="no_reason"
                    value={spouse.no_reason}
                    onChange={(e: Event) => onExplanationChange(e, index)}
                    required
                  />
                  {(() => {
                    const itemKey = `spouse-${index + 1}`;
                    const remark = spouseRemarks.find(
                      (r: any) => r.item_key === itemKey,
                    );
                    if (!remark || !remark.text || !uuid) return null;
                    return (
                      <ValidateText
                        uuid={uuid}
                        remark={remark}
                        validationId="022"
                        itemKey={itemKey}
                        formStateKey="autoPersonalForm"
                        context={getValidationMessage(validation)}
                      />
                    );
                  })()}
                </Box>
              )}

              {spouse.is_in_household === true && (
                <Box>
                  <Box mb="10px">
                    <RadioInput
                      label="Is this spouse licensed?"
                      subLabel={`Spouse ${index + 1}`}
                      section={SECTIONS.DRIVERS}
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      value={getRadioYesNo(spouse.is_licensed)}
                      onChange={(value) =>
                        onLicensedChange(index, value === 'true')
                      }
                      required
                    />
                  </Box>

                  {spouse.is_licensed === false && (
                    <Alert status="info" mb="10px" fontSize="14px">
                      <AlertIcon />
                      <AlertDescription>
                        A remark noting that the spouse is unlicensed will be
                        added to the application.
                      </AlertDescription>
                    </Alert>
                  )}

                  {spouse.is_licensed === true && (
                    <Flex gap="20px">
                      <FormInput
                        section={SECTIONS.DRIVERS}
                        label="License"
                        subLabel={`Spouse ${index + 1}`}
                        name="license_number"
                        value={spouse.license_number}
                        onChange={(e) => onSpousesChange(e, index)}
                        required
                      />
                      <FormInput
                        section={SECTIONS.DRIVERS}
                        label="Carrier"
                        subLabel={`Spouse ${index + 1}`}
                        name="carrier"
                        value={spouse.carrier}
                        onChange={(e) => onSpousesChange(e, index)}
                        required
                      />
                      <FormInput
                        section={SECTIONS.DRIVERS}
                        label="Policy #"
                        subLabel={`Spouse ${index + 1}`}
                        name="policy"
                        value={spouse.policy}
                        onChange={(e) => onSpousesChange(e, index)}
                        required
                      />
                      <DateInput
                        section={SECTIONS.DRIVERS}
                        label="Expiry Date"
                        subLabel={`Spouse ${index + 1}`}
                        date={getDateFromString(spouse.expiry_date)}
                        onChange={(date: Date) =>
                          onDateChange(date, 'expiry_date', index)
                        }
                        required
                      />
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default UnlistedSpouses;
